import { Handle, Node, NodeResizer, NodeToolbar, Position, useReactFlow, useStore } from '@xyflow/react';

import { useCallback, useState } from 'react';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';

import { useCopyToClipboard, useKeyDown } from '@@common';
import { ConfirmDialog } from '@@components';

import { ActivityMetadataModel } from '../../../../api/api';
import { ItemPresenceIndicator } from '../../../../components/Presence/PresenceContext';
import { useFlowMetadata } from '../../Hooks/Metadata';
import { ActivityModel } from '../../Models/FlowModel';
import { FlowEdge, FlowNode } from '../../Models/GraphModel';
import { getCachedAgentToolActivityIds } from '../../Services/AgentToolGraph';
import { ActivityCategories } from '../ActivityIcon';
import { ACTIVITY_HANDLES } from './ActivityHandles';
import './BaseNode.scss';
import ActivityValidationBadge from './ActivityValidationBadge';

const handleMargin = 0;
export type BaseNodeProps = {
	id: string;
	data: ActivityModel;
	selected?: boolean;
	activityMetadata: ActivityMetadataModel | undefined;
	/** CSS class applied to the inner container Stack, e.g. "activity-node" or "block-node" */
	nodeClassName: string;
	/** When true, renders a NodeResizer */
	resizable?: boolean;
	resizerVisible?: boolean;
	width?: number;
	height?: number;
	children: React.ReactNode;
};

export default function BaseNode({
	id,
	data,
	selected,
	activityMetadata,
	nodeClassName,
	resizable,
	resizerVisible = true,
	width,
	height,
	children,
}: BaseNodeProps) {
	const { getNodes, getEdges, setNodes, setEdges } = useReactFlow<FlowNode, FlowEdge>();
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const theme = useTheme();
	const handleStyle = {
		width: 10,
		height: 10,
		backgroundColor: theme.palette.primary.main,
		border: `2px solid ${theme.palette.divider}`,
		opacity: 0,
		transition: 'opacity 0.2s ease-in-out',
	};
	const copyToClipboard = useCopyToClipboard();
	const { clipboardService } = useFlowMetadata();

	const handleRemove = useCallback(() => {
		const allNodes = getNodes() as Node<ActivityModel>[];
		const idsToRemove = clipboardService.collectSubtreeIds(id, allNodes);
		setEdges((edges) => edges.filter((e) => !idsToRemove.has(e.source) && !idsToRemove.has(e.target)));
		setNodes((nodes) => nodes.filter((n) => !idsToRemove.has(n.id)));
		setShowDeleteConfirm(false);
	}, [id, clipboardService, getNodes, setEdges, setNodes]);

	const handleCopyData = useCallback(async () => {
		const allNodes = getNodes() as Node<ActivityModel>[];
		const allEdges = getEdges();
		const model = clipboardService.buildClipboardModel(id, allNodes, allEdges);
		if (!model) return;
		await copyToClipboard(model);
	}, [id, clipboardService, getNodes, getEdges, copyToClipboard]);

	const handleClone = useCallback(() => {
		const allNodes = getNodes() as Node<ActivityModel>[];
		const allEdges = getEdges();
		const { nodes: clonedNodes, edges: clonedEdges } = clipboardService.buildCloneResult(id, allNodes, allEdges);
		if (clonedNodes.length === 0) return;
		setNodes((nodes) => [...nodes, ...clonedNodes]);
		if (clonedEdges.length > 0) setEdges((edges) => [...edges, ...clonedEdges]);
	}, [id, clipboardService, getNodes, getEdges, setNodes, setEdges]);

	useKeyDown(
		['Delete', 'Backspace'],
		(event) => {
			const target = event.target as HTMLElement;
			const isReactFlowPane = target.classList.contains('react-flow__pane') || !!target.closest('.react-flow__pane');
			const isThisNode = !!target.closest(`.${nodeClassName.split(' ')[0]}`);
			if (!isReactFlowPane && !isThisNode) return;
			event.preventDefault();
			setShowDeleteConfirm(true);
		},
		selected,
	);

	useKeyDown(
		['c'],
		(event) => {
			if (!event.ctrlKey && !event.metaKey) return;
			const target = event.target as HTMLElement;
			const isInputField =
				target.tagName === 'INPUT' ||
				target.tagName === 'TEXTAREA' ||
				target.isContentEditable ||
				!!target.closest('input, textarea, [contenteditable="true"]');
			if (isInputField) return;
			if (!target.closest('.react-flow')) return;
			event.preventDefault();
			handleCopyData();
		},
		selected,
	);

	const isAgentService = activityMetadata?.categories?.includes(ActivityCategories.AGENT_SERVICE);
	const isAgentToolTarget = useStore((store) => getCachedAgentToolActivityIds(store.edges as FlowEdge[], store.nodes as FlowNode[]).has(id));
	const categoryClass = isAgentService
		? 'category-agent-service'
		: isAgentToolTarget
			? 'category-agent-tool'
			: activityMetadata?.categories?.includes(ActivityCategories.TRIGGER)
				? 'category-trigger'
				: data.Type === 'Start'
					? 'category-start'
					: data.Type === 'Exit'
						? 'category-exit'
						: activityMetadata?.categories?.includes(ActivityCategories.ERROR_HANDLING)
							? 'category-error-handling'
							: '';
	const isDisabledTrigger = categoryClass === 'category-trigger' && data.IsDisabled === true;

	return (
		<>
			{resizable && (
				<NodeResizer
					isVisible={selected && resizerVisible}
					minWidth={120}
					minHeight={80}
					handleStyle={{ width: 8, height: 8, backgroundColor: theme.palette.primary.main, borderColor: theme.palette.divider }}
					lineStyle={{ borderColor: theme.palette.primary.main }}
				/>
			)}

			<NodeToolbar position={Position.Bottom}>
				<Stack direction="row" spacing={1}>
					<IconButton onClick={handleClone} size="small" color="warning" title="Clone" sx={{ '&:hover': { backgroundColor: theme.palette.background.paper } }}>
						<ContentCopyIcon fontSize="small" />
					</IconButton>
					<IconButton
						onClick={handleCopyData}
						size="small"
						color="primary"
						title="Copy Data as JSON"
						sx={{ '&:hover': { backgroundColor: theme.palette.background.paper } }}
					>
						<ContentCopyIcon fontSize="small" />
					</IconButton>
					<IconButton
						onClick={() => setShowDeleteConfirm(true)}
						size="small"
						color="error"
						title="Remove"
						sx={{ '&:hover': { backgroundColor: theme.palette.background.paper } }}
					>
						<DeleteIcon fontSize="small" />
					</IconButton>
				</Stack>
			</NodeToolbar>

			{/* Target handles render first so source handles sit on top (source takes precedence when starting a connection) */}
			<Handle type="target" id={ACTIVITY_HANDLES.LEFT_TARGET} position={Position.Left} style={{ ...handleStyle, left: handleMargin }} />
			<Handle type="target" id={ACTIVITY_HANDLES.RIGHT_TARGET} position={Position.Right} style={{ ...handleStyle, right: handleMargin }} />
			<Handle type="target" id={ACTIVITY_HANDLES.TOP_TARGET} position={Position.Top} style={{ ...handleStyle, top: handleMargin }} />
			<Handle type="target" id={ACTIVITY_HANDLES.BOTTOM_TARGET} position={Position.Bottom} style={{ ...handleStyle, bottom: handleMargin }} />

			{/* Tool activities cannot be connection sources */}
			{!isAgentService && (
				<>
					<Handle type="source" id={ACTIVITY_HANDLES.LEFT_SOURCE} position={Position.Left} style={{ ...handleStyle, left: handleMargin }} />
					<Handle type="source" id={ACTIVITY_HANDLES.RIGHT_SOURCE} position={Position.Right} style={{ ...handleStyle, right: handleMargin }} />
					<Handle type="source" id={ACTIVITY_HANDLES.TOP_SOURCE} position={Position.Top} style={{ ...handleStyle, top: handleMargin }} />
					<Handle type="source" id={ACTIVITY_HANDLES.BOTTOM_SOURCE} position={Position.Bottom} style={{ ...handleStyle, bottom: handleMargin }} />
				</>
			)}

			<Stack
				direction="column"
				alignItems="center"
				className={`${nodeClassName} ${selected ? 'selected' : ''} ${categoryClass} ${isDisabledTrigger ? 'category-trigger-disabled' : ''}`.trim()}
				style={resizable ? { width: width, height: height, position: 'relative' } : { position: 'relative' }}
			>
				<Stack sx={{ position: 'absolute', top: -20, right: 0 }}>
					<ItemPresenceIndicator type="activity" id={id} />
				</Stack>
				{children}
				<ActivityValidationBadge activityId={id} />
			</Stack>

			<ConfirmDialog
				open={showDeleteConfirm}
				title="Remove Activity"
				message="Are you sure you want to remove this activity? All connected transitions will also be removed."
				confirmText="Remove"
				confirmColor="error"
				onConfirm={handleRemove}
				onCancel={() => setShowDeleteConfirm(false)}
			/>
		</>
	);
}
